import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react'
import RuleTable from './RuleTable'
import { AppContext } from 'Context/AppContext';
import { useNavigate } from "react-router";
import AuthManager from 'Common/AuthManager';
import NoDataComponent from 'Components/Common/NoDataComponent';
import { deleteRule, fetchRulesQueue, fetchRuleTableData, rerunRulesForIndustry } from './services/RuleService';
import usePagination from 'Components/Common/hooks/usePagination';
import { RuleContext } from './contexts/RuleContext';
import RulesQueue from 'Components/GMS/Rules/RulesQueue';
import { CSSTransition } from "react-transition-group";
import debounce from 'lodash/debounce';
import { ArsContext } from '../ARS/Contexts/ArsContext';
import { updateRecommenderModel } from '../ARS/ARSServices/ARSServices';
import ReloadButton from '../ARS/Components/ReloadButton';
import { RuleSortableColumn, SortType } from './enums';
import { hasValidModeTitles } from '../ARS/ModeTransformUtils';
import Button from 'Components/DesignSystem/Button/Button';
import Search from 'Components/DesignSystem/Search/Search';
import Divider from 'Components/DesignSystem/Divider/Divider';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Pagination from 'Components/DesignSystem/Pagination/Pagination';
import Typography from 'Components/DesignSystem/Typography/Typography';
import ConfirmDialog from 'Components/DesignSystem/ConfirmDialog/ConfirmDialog';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { ControlSize } from 'types/DesignSystem/controlSize';

/**
 * The page-size options, unchanged from `CSTablePagination`'s hardcoded `<option>`s.
 * `10000` is the legacy "All" sentinel the backend page-size API expects — the same
 * one the Guide list uses (§ 87).
 */
const PAGE_SIZE_OPTIONS = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
    { value: 10000, label: 'All' },
];

const RuleView: FC<RuleComponentProps> = ({ mode = 'default', baseCategories, targetCategories, onClose, onOpenCreateCompatibility }) => {
    const isSidePanel = mode !== 'default';
    const fromCategoryRelevancy = mode === 'category_relevancy';
    const appData = useContext(AppContext);
    const readPermission = appData.pm.checkPermission("gmsrules_read");
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const { newId } = useContext(RuleContext)
    const { totalRecords, handlePageChange, updatePageSize, paginationParams, updateTotalRecords } = usePagination(10)
    const [showRulesQueue, setShowRulesQueue] = useState<boolean>(false);
    const [rulesQueue, setRulesQueue] = useState<RuleQueueItem[]>([]);

    const [data, setData] = useState<Rule[]>([]);
    const [searchString, setSearchString] = useState<string>('');
    /* The DS Search is CONTROLLED, so the field's own text needs somewhere to live.
       `searchString` cannot serve — it is the debounced value that drives the fetch. */
    const [searchInput, setSearchInput] = useState<string>('');
    const [sortColumn, setSortColumn] = useState<RuleSortableColumn>(RuleSortableColumn.NAME);
    const [sortDirection, setSortDirection] = useState<SortType>(SortType.ASC);
    const rerunPermission = appData?.pm?.checkPermission("gmsrules_rerun");
    const [rerunLoading, setRerunLoading] = useState(false);
    /* AB#15856 — the rule the user asked to delete, held until they confirm. Delete
       used to call the API straight from the row menu, so one misclick removed a rule
       (and, through Decommit, its edges) with no way back. */
    const [pendingDelete, setPendingDelete] = useState<Rule | null>(null);
    const [deleting, setDeleting] = useState(false);
    const { model, setModel, industryId, channelKey, activeSubmodelIndex } = useContext(ArsContext);
    useEffect(() => {
        if (!readPermission && !fromCategoryRelevancy) {
            navigate("/workspace/module/permission/denied");
            return;
        }

        if (industryId)
            fetchTableData();
    }, [industryId, channelKey, paginationParams.page, paginationParams.per_page, searchString]);

    useEffect(() => {
        if (paginationParams.page !== 1)
            handlePageChange(1);
        else if (!loading) fetchTableData();
    }, [sortColumn, sortDirection])

    /* AB#15919 — the list is read from several effects (industry, channel, page, page
       size, search, sort), and their requests can overlap. Each answer used to be
       applied as it arrived, so a slow, older read (e.g. the page's first unfiltered
       load) could land after the searched one and replace it: the table then showed
       rows that did not match the search box, with nothing left to correct it. Only
       the answer to the latest request may update the table. */
    const latestListRequest = useRef(0);

    const fetchTableData = async () => {
        const requestId = ++latestListRequest.current;
        setLoading(true);
        let res = await fetchRuleTableData(
            industryId as string,
            channelKey as string,
            paginationParams.page,
            paginationParams.per_page,
            sortColumn,
            sortDirection,
            searchString,
            fromCategoryRelevancy ? undefined : isSidePanel,
            baseCategories,
            targetCategories
        )
        if (requestId !== latestListRequest.current) return; // a newer read is in flight
        setData(res?.data?.rules ?? []);
        updateTotalRecords(res?.data?.total ?? 0);
        if (!isSidePanel || fromCategoryRelevancy) fetchRulesQueueData();
        setLoading(false);
    };

    const fetchRulesQueueData = async () => {
        if (!industryId) return;
        let res = await fetchRulesQueue(industryId);
        setRulesQueue(res);
    };

    const handleRulesQueueVisibility = () => {
        setShowRulesQueue((prev) => {
            return !prev;
        });
    };

    /**
     * `useMemo`, and this is load-bearing — not tidying.
     *
     * It was a bare `debounce(...)` in the render body, which builds a NEW debounced
     * function on every render. That was survivable only because nothing in this
     * component re-rendered while typing: `CustomSearchBar` held the field's text in
     * its own state, so RuleView rendered once and its single debounced closure kept
     * its timer.
     *
     * Making the field controlled changes that. `setSearchInput` on every keystroke
     * re-renders RuleView, which would mint a fresh debounced function each time — so
     * each keystroke would start its own 500ms timer and every one of them would fire,
     * turning one search into N fetches with the last N-1 arriving out of order. One
     * stable instance across renders is what makes trailing-edge debouncing mean
     * anything. Cancelled on unmount so a pending timer cannot setState on a dead
     * component (§ 87 does the same).
     */
    const debouncedSearch = useMemo(
        () => debounce((value: string) => {
            setSearchString(value);
        }, 500),
        []
    );

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        debouncedSearch(value);
    };

    const handleDelete = async (rule: Rule) => {
        if (!appData?.pm?.checkPermission("gmsrules_delete")) {
            toast.info("You dont have permission for this operation")
            return
        }
        const permissionStatus = hasPermission(rule, 'delete');
        if (permissionStatus !== true && permissionStatus) {
            showNoPermissionToast(permissionStatus);
            return;
        }
        setPendingDelete(rule);
    }

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        setDeleting(true);
        try {
            await deleteRule(pendingDelete.id);
        } finally {
            setDeleting(false);
            setPendingDelete(null);
            fetchTableData();
        }
    }

    const hasPermission = (item: Rule, action: 'delete' | 'edit' | 'create' | 'copy' | 'view' | 'change_owner'): boolean | string => {

        const userobj = AuthManager.getAuthUser();
        const userProfile = userobj?.profile;
        if (!AuthManager.isAuthenticated() || !userProfile) return false;
        if (action === "change_owner") return true
        if (action === 'create' || action === 'copy' || action === 'view') { return true; }

        const userid = userProfile.id;

        if (userid !== item?.created_by_userid) {
            return `You can only ${action} rules that you have created`;
        }

        return true;
    }

    const handleActiveToggle = async (rule: Rule, newValue: boolean) => {
        if (!industryId || !isSidePanel) return;

        const submodel = model?.submodel[activeSubmodelIndex];

        if (submodel) {
            const rulesKey = mode === 'pure_compatibility' ? 'pure_compatibility_rules' : 'rules';
            const currentRules = submodel[rulesKey] || [];
            const updatedRules = [...currentRules];

            if (newValue) {
                if (!updatedRules.includes(rule.id)) {
                    updatedRules.push(rule.id);
                }
            } else {
                const ruleIndex = updatedRules.indexOf(rule.id);
                if (ruleIndex !== -1) {
                    updatedRules.splice(ruleIndex, 1);
                }
            }

            const updatedSubmodels = model.submodel.map((sm, index) =>
                index === activeSubmodelIndex
                    ? { ...sm, [rulesKey]: updatedRules }
                    : sm
            );

            const updatedModel = {
                ...model,
                submodel: updatedSubmodels
            };

            setModel(updatedModel);

            if (!hasValidModeTitles(model?.submodel ?? [])) {
                toast.error("All mode titles are required. Please ensure each mode has a valid title.");
                return;
            }

            const response = await updateRecommenderModel(updatedModel);

            if (!response?.done) {
                toast.error(response?.message ?? "Could not update the model");
                fetchTableData();
            }
        }
    };

    const handleEditAndCreateVersion = async (rule: Rule, type: 'create' | 'copy' | 'edit' | 'view') => {
        if (!appData?.pm?.checkPermission("gmsrules_create") || !appData?.pm?.checkPermission("gmsrules_update")) {
            toast.info("You dont have permission for this operation")
            return
        }
        const permissionStatus = hasPermission(rule, type);
        if (permissionStatus !== true && permissionStatus) {
            showNoPermissionToast(permissionStatus);
            return;
        }
        switch (type) {
            case 'edit':
                navigate(`/gms/rule/edit/${rule?.id}`);
                break;
            case 'copy':
                navigate(`/gms/rule/copy/${rule?.id}`);
                break
            case 'view':
                navigate(`/gms/rule/view/${rule?.id}`);
                break
            default:
                navigate(`/gms/rule/edit/0`);
                break;
        }
    };

    const handleClick = async (rule: Rule) => {
        const userobj = AuthManager.getAuthUser();
        const userProfile = userobj?.profile;
        if (!AuthManager.isAuthenticated() || !userProfile) return;
        const isRuleOwner = userProfile?.id === rule?.created_by_userid;
        const permissionStatus = hasPermission(rule, isRuleOwner ? 'edit' : 'view');
        if (permissionStatus !== true && permissionStatus) {
            showNoPermissionToast(permissionStatus);
            return;
        }
        navigate(`/gms/rule/${isRuleOwner ? 'edit' : 'view'}/${rule?.id}`);
    };

    const showNoPermissionToast = (message?: string) => {
        toast.error(message ?? "You dont have permission for this operation")
    }

    const handleRerunAll = async () => {
        if (!rerunPermission) {
            toast.info("You dont have permission for this operation");
            return;
        }
        if (!industryId) return;
        setRerunLoading(true);
        await rerunRulesForIndustry(industryId);
        setTimeout(() => {
            fetchRulesQueueData();
            setRerunLoading(false);
        }, 2000);
    };

    const handleCreate = () => {
        if (!appData?.pm?.checkPermission("gmsrules_create")) {
            toast.info("You dont have permission for this operation")
            return
        }
        navigate("/gms/rule/edit/0")
    }

    const fetchRulesQueueAndSetData = async () => {
        const data = await fetchRulesQueue(industryId as string)
        if (data) setRulesQueue(data)
        else toast.error("Couldnt retrieve Rules Queue data")
    }

    const queueCount = rulesQueue?.filter((x) => ["In Progress", "In Queue"].includes(x?.status))?.length ?? 0;

    /* § 265 — the four toolbar controls are NAMED here and ARRANGED by each surface.
       They were one shared `rulesToolbar` fragment until now, which is why the page
       header could not group anything without dragging the Category Relevancy panel
       along with it: the two surfaces have different shells (this page is a flex row
       with a spacer; the panel is `justify-between` around a 96%-wide title block).
       Nothing about what any control DOES changed — same handlers, same permission
       gate, same props — only which surface puts which control where. */

    const renderReload = (size: ControlSize) => (
        <ReloadButton loading={loading} onClick={() => fetchTableData()} label="Refresh rules" size={size} />
    );

    const renderSearch = (widthClass: string) => (
        <div className={widthClass}>
            <Search
                data-testid="rules-search-input"
                placeholder="Search Rules"
                ariaLabel="Search Rules"
                value={searchInput}
                onChange={handleSearchChange}
                /* § 288 — `loading` is the rules fetch (the same flag the Refresh
                   button and the Table take); the inequality is the 500ms debounce
                   before `searchString` commits. */
                loading={loading || searchInput !== searchString}
                loadingLabel="Searching rules"
                size="md"
            />
        </div>
    );

    /* Was a `<Link to="#">` — a navigation element wired to an onClick that
       navigates nowhere. It opens a panel, so it is a button (§ 108). The
       count travels INSIDE the accessible name rather than as a loose
       number beside it, because "Rule Action Queue" followed by an
       unlabelled "0" tells a screen-reader user nothing about what the
       zero counts. */
    /* § 111: the count goes in the Button's own badge SLOT, not in its
       children. Passed as a child it landed inside the inline `<span>`
       Button wraps children in, and a `display: flex` Badge in an inline
       span breaks to its own line and stretches — a full-width navy pill
       under the label. `Badge` counts things, `Status` states things
       (§ 51); the slot picks the variant that suits the button. */
    const renderQueueButton = (size: ControlSize) => (
        <Button
            variant="subtle"
            size={size}
            id="rules-link-action-queue"
            data-testid="rules-action-queue-btn"
            aria-label={`Rule Action Queue, ${queueCount} in progress`}
            onClick={handleRulesQueueVisibility}
            className="tw-whitespace-nowrap"
            showBadge
            badgeContent={queueCount}
            /* § 265 — the emphasis follows the count. `secondary` is the brand pill
               (`accent-bold` + `ink-inverse`), which for a queue of ZERO made the
               single loudest object in the toolbar a navy chip announcing that
               nothing is happening — the same navy as Create, at higher contrast,
               350px away from it. `neutral` (§ 208) is Badge's own answer to this:
               "a number that informs rather than demands". Nothing is hidden; the
               count still reads, at 9.3:1 in light and 7.1:1 in dark. NOT the
               `disabled` variant, which is 2.47:1 — legal only because a disabled
               control is exempt from WCAG 1.4.3, and this button is not disabled. */
            badgeVariant={queueCount > 0 ? 'secondary' : 'neutral'}
        >
            Rule Action Queue
        </Button>
    );

    const renderRecommitAll = () => rerunPermission && (
        <Button
            variant="secondary"
            id="rules-rerun-all"
            data-testid="rules-recommit-all-btn"
            className="tw-whitespace-nowrap"
            disabled={rerunLoading}
            onClick={handleRerunAll}
        >
            {rerunLoading ? "Re-Committing..." : "Re-Commit All"}
        </Button>
    );

    /* The Category Relevancy panel's arrangement, unchanged by § 265: one
       `#rules-toolbar` box holding refresh + queue at `sm`, then the 192px search,
       then Re-Commit All — the same three children, in the same order, with the
       same classes the shared fragment rendered. Only the page header below
       regroups. */
    const panelToolbar = (
        <>
            <div id="rules-toolbar" className="tw-flex tw-items-center tw-gap-2">
                {renderReload('sm')}
                {renderQueueButton('sm')}
            </div>
            {renderSearch('tw-w-48')}
            {renderRecommitAll()}
        </>
    );

    const tableContent = (
        /* `tw-mb-14` lived here only because the page scrolled: 56px of runway under a
           grid that ran off the bottom of <main>. The grid bounds itself now, so the
           margin is dead space between the last row and its own pager. */
        <div className="tw-w-[100%]" data-testid="rules-table">
            <RuleTable
                ruleList={data}
                loading={loading}
                handleDelete={handleDelete}
                handleEditAndCreateVersion={handleEditAndCreateVersion}
                fetchTableData={fetchTableData}
                handleClick={handleClick}
                NoDataComponent={NoDataComponent}
                handleActiveToggle={handleActiveToggle}
                isSidePanel={isSidePanel}
                sortColumn={sortColumn}
                setSortColumn={setSortColumn}
                sortDirection={sortDirection}
                setSortDirection={setSortDirection}
                mode={mode}
            />
            {!loading && data && data?.length > 0 ?
                <div id="rules-pagination-container" className="tw-py-2">
                    <div className="tw-flex tw-justify-end tw-items-center tw-gap-3 tw-flex-wrap">
                        {/* Was a raw `<select aria-label="Default select example">` — the
                            Bootstrap docs placeholder, shipped as the accessible name of
                            the rows-per-page control on every list page using
                            `CSTablePagination`. */}
                        {/* `usePortal` is not optional here (§ 111, the § 104 lesson).
                            Inline, the listbox is `absolute top-full` inside the page
                            flow: it opens DOWNWARD from a control that sits at the very
                            bottom of the page, so the options render past the fold and
                            the user has to scroll the whole page to reach them — and any
                            overflow ancestor clips them outright. Portalled, `Dropdown`
                            measures `window.innerHeight - rect.bottom` and anchors the
                            menu by its `bottom` when there is no room below, so it flips
                            UP and stays on screen. */}
                        <Dropdown
                            label=""
                            aria-label="Rows per page"
                            size="sm"
                            className="tw-w-[84px]"
                            usePortal
                            options={PAGE_SIZE_OPTIONS}
                            value={paginationParams?.per_page ?? 10}
                            onChange={(value) => updatePageSize(Number(value))}
                            data-testid="rules-page-size"
                        />
                        <Pagination
                            page={paginationParams?.page}
                            totalItems={totalRecords}
                            pageSize={paginationParams?.per_page}
                            onPageChange={handlePageChange}
                            size="sm"
                            data-testid="rules-pagination"
                        />
                    </div>
                </div>
                : null
            }
        </div>
    );

    if (fromCategoryRelevancy) {
        return (
            <>
                {/* Backdrop -- matches the panel's own footprint (right of the 220px sidebar,
                    below the 60px header), not the full viewport. inset-0 here would sit above
                    the sidebar too (it's fixed-positioned with a z-index, so it stacks above the
                    statically-positioned sidebar regardless of DOM order) and swallow every
                    sidebar click as "close panel" instead of letting it navigate. */}
                <div data-testid="rules-ruleview-backdrop"
                    className="tw-fixed tw-top-[60px] tw-right-0 tw-bottom-0 tw-left-[220px] tw-z-40"
                    onClick={onClose}
                    aria-hidden="true"
                />
                {/* Panel */}
                <div
                    className="tw-fixed tw-right-0 tw-top-[60px] tw-bottom-0 tw-bg-surface tw-shadow-[0_4px_24px_rgba(0,0,0,0.18)] tw-z-50 tw-flex tw-flex-col"
                    style={{ width: 'calc(100vw - 220px)' }}
                >
                    <div className="tw-px-[21px] tw-pt-[12px] tw-flex tw-justify-between">
                        <div className="tw-flex tw-flex-col tw-items-left tw-text-xs tw-w-[96%]">
                            {/* Was the same `<label>`-as-title as the main page. This
                                surface is a panel, so the heading is an h2. */}
                            <Typography className="tw-text-ink" variant="heading-2" as="h2" data-testid="rules-panel-title">
                                View Compatibility
                            </Typography>
                        </div>
                        {panelToolbar}
                        <div className="tw-flex tw-items-center tw-gap-2 tw-ps-3">
                            <Button data-testid="rules-ruleview-create-btn" variant="primary" onClick={() => onOpenCreateCompatibility?.()}>Create</Button>
                            <Button data-testid="rules-ruleview-cancel-btn" variant="secondary" onClick={() => onClose?.()}>Cancel</Button>
                        </div>
                    </div>
                    {/* Content */}
                    <div className="tw-flex-1 tw-overflow-y-auto tw-px-6 tw-py-4">
                        {tableContent}
                    </div>
                    {showRulesQueue && (
                        <CSSTransition
                            timeout={1000}
                            key={String(showRulesQueue)}
                            classNames={{
                                enterActive: "animate__animated animate__flipInX",
                                exitActive: "animate__animated animate__flipOutX",
                            }}
                        >
                            <RulesQueue
                                setShowRulesQueue={setShowRulesQueue}
                                rulesQueue={rulesQueue}
                                fetchRulesQueue={fetchRulesQueueAndSetData}
                            />
                        </CSSTransition>
                    )}
                </div>
            </>
        );
    }

    return (
        <>
            <div id="rules-page" data-testid="rules-page" className="pt-0 tw-w-full tw-mt-0">

                {/* § 266 — the page's horizontal inset lives HERE, once, so every band
                    shares one box: header, divider, grid and pagination. It used to sit
                    on `#rules-header` alone, which made the header an inset island over
                    full-bleed everything-else — the h1 hung right of the "Rule Name"
                    column it heads and Create's right edge stopped 21px short of the
                    grid's.

                    § 267 — and the number is ZERO, not the 21px § 266 first shipped.
                    `tw-px-[21px]` looked like an app convention because six page headers
                    carry the literal, but it is drift, not a system: `ProductCategoryList`
                    pairs it with a `tw-ml-5` divider (20px) over content at 0 — three
                    numbers on one page. Every GRID in the app sits at 0, the Guide and
                    Manage Party lists are internally consistent at 0, and the page gutter
                    comes from the shell. So the header came in to meet the content rather
                    than the content going out to meet the header.

                    The element stays even at 0: what § 266 actually fixed is that ONE
                    place owns this number and every band is its descendant, which is the
                    part that keeps a later sibling from drifting. And it is deliberately
                    NOT on the grid's own scroll container, where a padding-right is
                    dropped at the scroll end in several engines. */}
                <div id="rules-page-inset" className="tw-px-0 tw-overflow-x-hidden">
                    {!isSidePanel && (
                        /* `CSConfigHeader` is still used by the CS pages, so it stays —
                           this page just stops consuming it. What it gave us was a
                           `<label>` acting as the page title, so `/gms/compatibilities`
                           had NO heading element at all: nothing for a screen reader to
                           jump to, and nothing in the document outline. */
                        /* § 265 — the row states its own gaps instead of carrying
                           `tw-gap-2`, because two of them are no longer 8px: the
                           groups below need 16px and 24px, and a row gap underneath
                           a margin makes every number here arithmetic to read. */
                        <div id="rules-header" className="tw-pt-[12px] tw-flex tw-items-center">
                            <Typography className="tw-text-ink" variant="display" as="h1" id="rules-title" data-testid="rules-page-title">
                                Compatibility
                            </Typography>
                            {/* § 265 — search and refresh act on the table below, so they
                                belong to "what am I looking at", not "what can I do". The
                                header had 437px of dead space here — 35% of the row —
                                while five controls queued at a uniform 8px pitch across
                                the right half. The search also grows 192px → 288px, the
                                width the other list page in this app searches at; at 192
                                the input was 138px and cut a rule name at ~18 characters. */}
                            <div id="rules-view-controls" className="tw-flex tw-items-center tw-gap-2 tw-ms-4">
                                {renderSearch('tw-w-72 tw-min-w-0')}
                                {/* `md`, not `sm`. The control grid
                                    (`DesignSystem/controlSize.ts`) is 28/36/44 and exists
                                    so side-by-side controls share an edge; this row used
                                    two rungs at once, so its five controls shared a centre
                                    line and nothing else — tops at 12/13/16, bottoms at
                                    44/47/48. Every control in the row is 36 now. */}
                                {renderReload('md')}
                            </div>
                            <div className="tw-flex tw-flex-grow" />
                            {/* § 265 — the queue opens a panel: a utility, not an action on
                                the data. It keeps the quiet rung and stands on its own,
                                before the break. At `md` its 28px badge is finally smaller
                                than its host — inside a 28px `sm` button the pill was
                                exactly as tall as the button, which is not a badge. */}
                            {renderQueueButton('md')}
                            {/* § 265 — 24px against the row's 8px: proximity is what
                                separates the page's utilities from the actions on its data.
                                It sits on the action group, not on the queue button's
                                margin, so the break survives `rerunPermission` being false,
                                and `shrink-0` keeps the two CTAs off the list of things
                                that squash first. */}
                            <div id="rules-actions" className="tw-flex tw-items-center tw-gap-2 tw-ms-6 tw-shrink-0">
                                {renderRecommitAll()}
                                <Button
                                    variant="primary"
                                    id="rules-create-btn"
                                    data-testid="config-header-action-btn"
                                    disabled={!appData?.pm?.checkPermission("gmsrules_create")}
                                    onClick={handleCreate}
                                >
                                    Create
                                </Button>
                            </div>
                        </div>
                    )}
                    <Divider data-testid="rules-header-divider" className="tw-mt-2" />
                    {tableContent}
                </div>
                {showRulesQueue && (
                    <CSSTransition
                        timeout={1000}
                        key={String(showRulesQueue)}
                        classNames={{
                            enterActive: "animate__animated animate__flipInX",
                            exitActive: "animate__animated animate__flipOutX",
                        }}
                    >
                        <RulesQueue
                            setShowRulesQueue={setShowRulesQueue}
                            rulesQueue={rulesQueue}
                            fetchRulesQueue={fetchRulesQueueAndSetData}
                        />
                    </CSSTransition>
                )}
                {/* AB#15856 — Delete confirms first. Only this page renders the row
                    menu (the side panel has no actions column), so only this return
                    needs the dialog. */}
                <ConfirmDialog
                    open={pendingDelete !== null}
                    title="Delete rule"
                    message={`Delete the rule "${pendingDelete?.name ?? ''}"?`}
                    secondaryMessage="This cannot be undone."
                    confirmText="Delete"
                    tone="danger"
                    loading={deleting}
                    busyText="Deleting..."
                    onConfirm={confirmDelete}
                    onCancel={() => setPendingDelete(null)}
                    data-testid="rules-delete-confirm"
                />
            </div>
        </>
    )
}

export default RuleView
